import React, { useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@apollo/client";
import { Link, useNavigate } from "react-router-dom";
import { GET_ALL_GISTS, GET_ALL_SUBJECTS } from "../../services/graphql/queriesMutations";
import styles from "./SubjectsLabPage.module.css";

type LabSubject = {
  id: number;
  title: string;
  parentId: number | null;
};

type LabGist = {
  id: number;
  title: string;
  parentId?: number | null;
  subjectId?: number | null;
};

type SubjectsQuery = {
  subjects: LabSubject[];
};

type GistsQuery = {
  gists: LabGist[];
};

type SubjectStats = {
  directChildSubjects: number;
  totalDescendantSubjects: number;
  directGists: number;
  rootGists: number;
};

type SubjectNode = {
  subject: LabSubject;
  children: SubjectNode[];
  depth: number;
  directChildSubjects: number;
  totalDescendantSubjects: number;
  directGists: number;
  rootGists: number;
};

type PositionedSubjectNode = SubjectNode & {
  x: number;
  y: number;
};

type PositionedLink = {
  id: string;
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  hot: boolean;
};

type SubjectLayout = {
  nodes: PositionedSubjectNode[];
  links: PositionedLink[];
  canvasWidth: number;
  canvasHeight: number;
};

const CARD_WIDTH = 310;
const CARD_HEIGHT = 206;
const NUCLEUS_X = 70;
const NUCLEUS_Y = 80;
const FIRST_SUBJECT_X = 560;
const COLUMN_GAP = 440;
const ROW_GAP = 244;
const TOP_ALIGNED_Y = 80;
const MIN_CANVAS_WIDTH = 1600;
const MIN_CANVAS_HEIGHT = 960;

function sortSubjects(subjects: LabSubject[]): LabSubject[] {
  return subjects.slice().sort((a, b) => a.title.localeCompare(b.title));
}

function buildSubjectMap(subjects: LabSubject[]): { [id: number]: LabSubject } {
  const byId: { [id: number]: LabSubject } = {};
  subjects.forEach((subject) => {
    byId[subject.id] = subject;
  });
  return byId;
}

function buildSubjectBuckets(subjects: LabSubject[]): { [parentKey: string]: LabSubject[] } {
  const byId = buildSubjectMap(subjects);
  const buckets: { [parentKey: string]: LabSubject[] } = {};

  subjects.forEach((subject) => {
    const parentExists = typeof subject.parentId === "number" && Boolean(byId[subject.parentId]);
    const key = parentExists ? String(subject.parentId) : "root";
    buckets[key] = buckets[key] || [];
    buckets[key].push(subject);
  });

  Object.keys(buckets).forEach((key) => {
    buckets[key] = sortSubjects(buckets[key]);
  });

  return buckets;
}

function buildGistParentMap(gists: LabGist[]): { [id: number]: LabGist } {
  const byId: { [id: number]: LabGist } = {};
  gists.forEach((gist) => {
    byId[gist.id] = gist;
  });
  return byId;
}

function getGistCountsBySubject(gists: LabGist[]): { [subjectId: number]: { directGists: number; rootGists: number } } {
  const counts: { [subjectId: number]: { directGists: number; rootGists: number } } = {};
  const gistById = buildGistParentMap(gists);

  gists.forEach((gist) => {
    if (typeof gist.subjectId !== "number") return;
    counts[gist.subjectId] = counts[gist.subjectId] || { directGists: 0, rootGists: 0 };
    counts[gist.subjectId].directGists += 1;

    const parent = typeof gist.parentId === "number" ? gistById[gist.parentId] : null;
    const parentInSameSubject = parent && parent.subjectId === gist.subjectId;
    if (!parentInSameSubject) {
      counts[gist.subjectId].rootGists += 1;
    }
  });

  return counts;
}

function countDescendantSubjects(subject: LabSubject, buckets: { [parentKey: string]: LabSubject[] }): number {
  let total = 0;
  const visit = (parentId: number) => {
    (buckets[String(parentId)] || []).forEach((child) => {
      total += 1;
      visit(child.id);
    });
  };
  visit(subject.id);
  return total;
}

function buildSubjectTree(subjects: LabSubject[], gists: LabGist[]): SubjectNode[] {
  const buckets = buildSubjectBuckets(subjects);
  const gistCounts = getGistCountsBySubject(gists);

  const buildNode = (subject: LabSubject, depth: number): SubjectNode => {
    const children = (buckets[String(subject.id)] || []).map((child) => buildNode(child, depth + 1));
    const counts = gistCounts[subject.id] || { directGists: 0, rootGists: 0 };
    return {
      subject,
      children,
      depth,
      directChildSubjects: children.length,
      totalDescendantSubjects: countDescendantSubjects(subject, buckets),
      directGists: counts.directGists,
      rootGists: counts.rootGists,
    };
  };

  return (buckets.root || []).map((subject) => buildNode(subject, 0));
}

function flattenNodes(nodes: SubjectNode[]): SubjectNode[] {
  const flat: SubjectNode[] = [];
  const visit = (node: SubjectNode) => {
    flat.push(node);
    node.children.forEach((child) => visit(child));
  };
  nodes.forEach((node) => visit(node));
  return flat;
}

function layoutSubjectTree(roots: SubjectNode[]): SubjectLayout {
  const nodes: PositionedSubjectNode[] = [];
  const links: PositionedLink[] = [];
  const nextYByDepth: { [depth: number]: number } = {};

  const layoutNode = (node: SubjectNode): PositionedSubjectNode => {
    const depth = node.depth;
    const y = nextYByDepth[depth] ?? TOP_ALIGNED_Y;
    nextYByDepth[depth] = y + ROW_GAP;

    const positioned: PositionedSubjectNode = {
      ...node,
      x: FIRST_SUBJECT_X + depth * COLUMN_GAP,
      y,
    };

    nodes.push(positioned);

    node.children.forEach((child) => {
      const childPosition = layoutNode(child);
      links.push({
        id: `${positioned.subject.id}-${child.subject.id}`,
        fromX: positioned.x + CARD_WIDTH,
        fromY: positioned.y + Math.round(CARD_HEIGHT * 0.46),
        toX: childPosition.x,
        toY: childPosition.y + Math.round(CARD_HEIGHT * 0.46),
        hot: positioned.depth === 0,
      });
    });

    return positioned;
  };

  roots.forEach((root) => {
    const rootPosition = layoutNode(root);
    links.push({
      id: `all-subjects-${rootPosition.subject.id}`,
      fromX: NUCLEUS_X + 340,
      fromY: NUCLEUS_Y + Math.round(CARD_HEIGHT * 0.46),
      toX: rootPosition.x,
      toY: rootPosition.y + Math.round(CARD_HEIGHT * 0.46),
      hot: true,
    });
  });

  const maxX = nodes.length ? Math.max.apply(null, nodes.map((node) => node.x)) : FIRST_SUBJECT_X;
  const maxY = nodes.length ? Math.max.apply(null, nodes.map((node) => node.y)) : 180;

  return {
    nodes,
    links,
    canvasWidth: Math.max(MIN_CANVAS_WIDTH, maxX + CARD_WIDTH + 260),
    canvasHeight: Math.max(MIN_CANVAS_HEIGHT, maxY + CARD_HEIGHT + 190),
  };
}

function getSubjectPath(subjects: LabSubject[], subjectId: number | null): LabSubject[] {
  if (!subjectId) return [];
  const byId = buildSubjectMap(subjects);
  const path: LabSubject[] = [];
  let current: LabSubject | undefined = byId[subjectId];
  let guard = 0;

  while (current && guard < 80) {
    path.unshift(current);
    current = current.parentId === null ? undefined : byId[current.parentId];
    guard += 1;
  }

  return path;
}

function getDepthLabel(depth: number): string {
  if (depth === 0) return "Root Subject";
  if (depth === 1) return "Child Subject · level 1";
  return `Child Subject · level ${depth}`;
}

function getToneClass(depth: number): string {
  if (depth === 0) return styles.rootSubjectCard;
  if (depth === 1) return styles.childSubjectCard;
  if (depth === 2) return styles.deepSubjectCard;
  return styles.deeperSubjectCard;
}

function getSelectedStats(node: SubjectNode | null): SubjectStats | null {
  if (!node) return null;
  return {
    directChildSubjects: node.directChildSubjects,
    totalDescendantSubjects: node.totalDescendantSubjects,
    directGists: node.directGists,
    rootGists: node.rootGists,
  };
}

const SubjectsLabPage: React.FC = () => {
  const navigate = useNavigate();
  const { data: subjectData, loading: subjectsLoading, error: subjectsError } = useQuery<SubjectsQuery>(GET_ALL_SUBJECTS, {
    fetchPolicy: "cache-and-network",
  });
  const { data: gistData, loading: gistsLoading, error: gistsError } = useQuery<GistsQuery>(GET_ALL_GISTS, {
    fetchPolicy: "cache-and-network",
  });

  const subjects = useMemo(() => subjectData?.subjects || [], [subjectData]);
  const gists = useMemo(() => gistData?.gists || [], [gistData]);
  const tree = useMemo(() => buildSubjectTree(subjects, gists), [subjects, gists]);
  const flatNodes = useMemo(() => flattenNodes(tree), [tree]);
  const layout = useMemo(() => layoutSubjectTree(tree), [tree]);

  const [selectedSubjectId, setSelectedSubjectId] = useState<number | null>(null);
  const [openedSubjectId, setOpenedSubjectId] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef({ startX: 0, startY: 0, startLeft: 0, startTop: 0 });

  useEffect(() => {
    if (selectedSubjectId || !flatNodes.length) return;
    setSelectedSubjectId(flatNodes[0].subject.id);
  }, [flatNodes, selectedSubjectId]);

  const selectedNode = selectedSubjectId ? flatNodes.find((node) => node.subject.id === selectedSubjectId) || null : null;
  const openedNode = openedSubjectId ? flatNodes.find((node) => node.subject.id === openedSubjectId) || null : null;
  const selectedPath = getSubjectPath(subjects, selectedSubjectId);
  const openedPath = getSubjectPath(subjects, openedSubjectId);
  const selectedStats = getSelectedStats(selectedNode);
  const loading = subjectsLoading || gistsLoading;
  const error = subjectsError || gistsError;

  const handleCanvasMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button, a, .subjects-sheet-lab-card")) return;
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    dragStateRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      startLeft: scrollEl.scrollLeft,
      startTop: scrollEl.scrollTop,
    };
    setIsDragging(true);
    event.preventDefault();
  };

  useEffect(() => {
    if (!isDragging) return;

    const handleMouseMove = (event: MouseEvent) => {
      const scrollEl = scrollRef.current;
      if (!scrollEl) return;
      const state = dragStateRef.current;
      scrollEl.scrollLeft = state.startLeft - (event.clientX - state.startX);
      scrollEl.scrollTop = state.startTop - (event.clientY - state.startY);
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);
    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  const resetSheet = () => {
    scrollRef.current?.scrollTo({ left: 0, top: 0, behavior: "smooth" });
  };

  const openGistSheetForSubject = (subjectId: number) => {
    navigate(`/gist-lab?subjectId=${subjectId}`);
  };

  const focusSelected = () => {
    if (!selectedNode || !scrollRef.current) return;
    scrollRef.current.scrollTo({
      left: Math.max(0, selectedNode.depth * COLUMN_GAP + FIRST_SUBJECT_X - 140),
      top: Math.max(0, (layout.nodes.find((node) => node.subject.id === selectedNode.subject.id)?.y || 0) - 120),
      behavior: "smooth",
    });
  };

return (
    <main className={styles.pageShell}>
      <section className={styles.labFrame}>
        <header className={styles.header}>
          <div>
            <p className={styles.eyebrow}>Sandbox · removable page · real Subject hierarchy</p>
            <h1>Subjects Infinite Sheet</h1>
            <p className={styles.intro}>
              This page shows real Subjects as an infinite parent → child sheet. Click any Subject card to open Gist Lab filtered to that Subject.
            </p>
          </div>
          <div className={styles.headerPills}>
            <span>{tree.length} root Subjects</span>
            <span>{flatNodes.length} total Subjects</span>
            <span>{gists.length} Gists counted</span>
          </div>
        </header>

        <section className={styles.sheetPanel}>
          <div className={styles.sheetToolbar}>
            <div className={styles.pathBlock}>
              <span className={styles.pathLabel}>Selected subject</span>
              <strong>{selectedNode?.subject.title || "No subject selected"}</strong>
              <em>{selectedPath.map((subject) => subject.title).join(" / ") || "Click a Subject card to open Gist Lab"}</em>
            </div>
            <div className={styles.actions}>
              <button type="button" className={styles.actionButton} onClick={focusSelected}>
                Focus selected
              </button>
              <button type="button" className={styles.actionButton} onClick={resetSheet}>
                Reset sheet
              </button>
            </div>
          </div>

          {selectedStats && (
            <div className={styles.selectedStats}>
              <span>{selectedStats.directChildSubjects} direct child Subjects</span>
              <span>{selectedStats.totalDescendantSubjects} total descendant Subjects</span>
              <span>{selectedStats.rootGists} root Gists</span>
              <span>{selectedStats.directGists} total direct Gists</span>
            </div>
          )}

          {loading && <div className={styles.loadingBlock}>Loading Subjects and Gist counts…</div>}
          {error && <div className={styles.errorBlock}>Could not load data: {error.message}</div>}

          <div
            ref={scrollRef}
            className={`${styles.sheetScroll} ${isDragging ? styles.sheetScrollDragging : ""}`}
            onMouseDown={handleCanvasMouseDown}
          >
            <div className={styles.sheetCanvas} style={{ width: layout.canvasWidth, height: layout.canvasHeight }}>
              <svg className={styles.links} width={layout.canvasWidth} height={layout.canvasHeight} viewBox={`0 0 ${layout.canvasWidth} ${layout.canvasHeight}`}>
                {layout.links.map((link) => {
                  const midX = Math.round((link.fromX + link.toX) / 2);
                  return (
                    <path
                      key={link.id}
                      className={`${styles.linkPath} ${link.hot ? styles.linkPathHot : ""}`}
                      d={`M ${link.fromX} ${link.fromY} C ${midX} ${link.fromY}, ${midX} ${link.toY}, ${link.toX} ${link.toY}`}
                    />
                  );
                })}
              </svg>

              <article className={`${styles.subjectNucleus} subjects-sheet-lab-card`} style={{ left: NUCLEUS_X, top: NUCLEUS_Y }}>
                <span>Subject universe</span>
                <h2>All Subjects</h2>
                <p>
                  This is the starting nucleus. First-level cards are root Subjects; deeper cards are real child Subjects from parentId.
                </p>
                <div className={styles.cardChips}>
                  <span>{tree.length} root Subjects</span>
                  <span>{flatNodes.length} total Subjects</span>
                </div>
              </article>

              {flatNodes.length === 0 && !loading && (
                <div className={styles.emptySheet}>
                  <h2>No Subjects found.</h2>
                  <p>The query returned no Subjects. The page is read-only and did not modify anything.</p>
                </div>
              )}

              {layout.nodes.map((node) => {
                const isSelected = node.subject.id === selectedSubjectId;
                return (
                  <article
                    key={node.subject.id}
                    className={`${styles.subjectCard} ${getToneClass(node.depth)} ${isSelected ? styles.subjectCardSelected : ""} subjects-sheet-lab-card`}
                    style={{ left: node.x, top: node.y }}
                    onClick={() => openGistSheetForSubject(node.subject.id)}
                    title={`Open Gist Lab for ${node.subject.title}`}
                  >
                    <div className={styles.cardKicker}>{getDepthLabel(node.depth)} · #{node.subject.id}</div>
                    <h3>{node.subject.title}</h3>
                    <p>
                      {node.children.length > 0
                        ? `${node.children.length} direct child Subjects continue from this card.`
                        : "This Subject has no child Subjects in the current tree."}
                    </p>
                    <div className={styles.cardChips}>
                      <span>{node.directChildSubjects} child Subjects</span>
                      <span>{node.totalDescendantSubjects} descendants</span>
                      <span>{node.rootGists} root Gists</span>
                      <span>{node.directGists} direct Gists</span>
                    </div>
                    <button type="button" className={styles.openButton} onClick={(event) => { event.stopPropagation(); setOpenedSubjectId(node.subject.id); }}>
                      Preview
                    </button>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      </section>

      {openedNode && (
        <section className={styles.overlay} onClick={() => setOpenedSubjectId(null)}>
          <article className={styles.openedSubject} onClick={(event) => event.stopPropagation()}>
            <button type="button" className={styles.closeButton} onClick={() => setOpenedSubjectId(null)}>
              Close
            </button>
            <p className={styles.eyebrow}>Read-only Subject preview · {getDepthLabel(openedNode.depth)}</p>
            <h2>{openedNode.subject.title}</h2>
            <p className={styles.openedPath}>{openedPath.map((subject) => subject.title).join(" / ") || "No path"}</p>
            <div className={styles.openedGrid}>
              <div><span>Direct child Subjects</span><strong>{openedNode.directChildSubjects}</strong></div>
              <div><span>Total descendant Subjects</span><strong>{openedNode.totalDescendantSubjects}</strong></div>
              <div><span>Root Gists</span><strong>{openedNode.rootGists}</strong></div>
              <div><span>Total direct Gists</span><strong>{openedNode.directGists}</strong></div>
            </div>
            <p className={styles.openedNote}>
              This page is only a sandbox presentation of the Subject hierarchy. It does not rename, move, create, or delete Subjects.
            </p>
            <div className={styles.openedFooter}>
              <Link to="/" className={styles.primaryLink}>Open production Subject Tree</Link>
            </div>
          </article>
        </section>
      )}
    </main>
  );
};

export default SubjectsLabPage;
